import { Navigate, Outlet, useLocation, useParams } from "react-router";
import { useSession } from "@/lib/store/session";
import { useUser } from "@/lib/query/useUser";
import { FullPageLoader } from "@/components/FullPageLoader";

/** Unauthenticated users go to /auth (or the public view of an activity). */
export function RequireAuth() {
  const userId = useSession((s) => s.userId);
  const location = useLocation();
  const params = useParams();
  if (!userId) {
    if (location.pathname.startsWith("/runs/") && params.id) {
      return <Navigate to={`/viewactivity/${params.id}`} replace />;
    }
    return <Navigate to={`/auth?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  return <Outlet />;
}

/** Pushes a new account through the two onboarding steps before the main app. */
export function RequireSetup() {
  const { data: user, isLoading, isError } = useUser();
  const location = useLocation();
  if (isLoading) return <FullPageLoader />;
  if (isError || !user) return <Outlet />; // let the page surface the error
  if (!user.prefsSet && !location.pathname.startsWith("/onboarding")) {
    return <Navigate to="/onboarding/account" replace />;
  }
  if (!user.synchSet && !location.pathname.startsWith("/onboarding")) {
    return <Navigate to="/onboarding/synch" replace />;
  }
  return <Outlet />;
}

/** Logged-in users never see the auth pages. */
export function RedirectIfAuthed() {
  const userId = useSession((s) => s.userId);
  const location = useLocation();
  // The Garmin callback must always be allowed to run. OAuth2 PKCE returns
  // code/state (#285); the OAuth1 params are still matched so a callback that
  // was in flight across the deploy still completes.
  const params = new URLSearchParams(location.search);
  const isGarminCallback =
    params.has("code") || params.has("state") || params.has("error") || params.has("oauth_token");
  if (userId && !isGarminCallback) return <Navigate to="/home" replace />;
  return <Outlet />;
}
